<template>
  <div>
    <div
      v-if="errorMessage"
      class="card text-center bg-red text-white max-w-sm mx-auto p-2 mb-4"
    >
      <p
        class="text-white"
        v-text="errorMessage"
      />
    </div>
    <form @submit.prevent="submit">
      <div class="max-w-sm mx-auto p-6">
        <div class="sm:flex">
          <div class="w-full sm:w-1/2 sm:mr-2 mb-4">
            <label
              for="first_name"
              v-text="'First name'"
            />
            <input
              id="first_name"
              v-model="request.first_name"
              class="input"
              :class="{ 'error': hasErrorFor('first_name') }"
              style="margin-bottom: .25rem;"
              placeholder="First Name"
              name="first_name"
              type="text"
              required
            >
            <span
              v-if="hasErrorFor('first_name')"
              class="field__meta__help"
              v-text="getErrorFor('first_name')"
            />
          </div>
          <div class="w-full sm:w-1/2 sm:ml-2 mb-4">
            <label
              for="last_name"
              v-text="'Last name'"
            />
            <input
              id="last_name"
              v-model="request.last_name"
              class="input"
              :class="{ 'error': hasErrorFor('last_name') }"
              style="margin-bottom: .25rem;"
              placeholder="Last Name"
              name="last_name"
              type="text"
              required
            >
            <span
              v-if="hasErrorFor('last_name')"
              class="field__meta__help"
              v-text="getErrorFor('last_name')"
            />
          </div>
        </div>
        <div class="mb-4">
          <label
            for="email"
            v-text="'Email (No department emails)'"
          />
          <input
            id="email"
            v-model="request.email"
            class="input"
            :class="{ 'error': hasErrorFor('email') }"
            style="margin-bottom: .25rem;"
            name="email"
            placeholder="Email Address"
            type="email"
            required
          >
          <span
            v-if="hasErrorFor('email')"
            class="field__meta__help"
            v-text="getErrorFor('email')"
          />
        </div>

        <div class="mb-4">
          <label
            for="phone_number"
            v-text="'Mobile Number'"
          />
          <VuePhoneNumberInput
            id="phone_number"
            v-model="request.phone_number"
            color="#064bc3"
            class="mb-4"
            :translations="{ phoneNumberLabel: 'Mobile Number' }"
            default-country-code="US"
            no-flags
            no-validator-state
            required
            @update="handlePhoneUpdate"
          />
          <div
            v-if="hasErrorFor('phone_number')"
            class="field__meta pb-6"
          >
            <span
              class="field__meta__help"
              v-html="getErrorFor('phone_number')"
            />
          </div>
          <AppCheckboxInput
            id="opt_in_to_texts"
            v-model="request.opt_in_to_texts"
            name="opt_in_to_texts"
            label="Opt in to receive text messages"
            container-classes="mb-6"
          />
        </div>
        <div
          v-if="selfAssignableGroupsEnabled"
          class="mb-4"
        >
          <div class="w-full">
            <label
              for="user_groups"
              v-text="'User Groups'"
            />
            <Multiselect
              id="user_groups"
              v-model="request.groups.nonRank"
              :multiple="true"
              :loading="isLoadingNonRankGroups"
              :options="nonRankGroups"
              label="title"
              placeholder="Select groups"
              track-by="uuid"
              @search-change="handleNonRankSearchChange"
            />
          </div>
        </div>
        <FormBuilderFieldSet
          v-for="element in formBuilderForms"
          :key="`form-${element.form.uuid}`"
          :form="element.form"
          :show-outline="false"
          :show-title="false"
          :submission="element.submission"
          :union-slug="slug"
          :flat-picker-date-format="flatPickerDateFormat"
          class="mb-8"
          @submissionUpdated="updateSubmission"
        />
        <PaymentForm
          v-if="hasMembershipFee"
          :run-confirm-card-setup="runPaymentSetup"
          is-access-request
          @cardSetupConfirmed="handleCardSetupConfirmed"
          @cardSetupError="handleCardSetupError"
        />
        <div class="text-center m-6">
          <button
            class="btn btn-blue"
            type="submit"
            :disabled="processing"
            :aria-disabled="processing"
            :class="{ load: processing }"
            v-text="'Request Access'"
          />
        </div>
      </div>
    </form>
  </div>
</template>

<script type="text/babel">
import { debounce } from 'lodash'
import Multiselect from 'vue-multiselect'
import VuePhoneNumberInput from 'vue-phone-number-input'
import 'vue-phone-number-input/dist/vue-phone-number-input.css'

import Form from 'Base/Resources/assets/js/plugins/Form'
import {subDomainParser} from 'Base/Resources/assets/js/plugins/UrlParser'
import PaymentForm from 'Payments/Resources/assets/js/components/MembershipFeePaymentForm'
import AppCheckboxInput from 'Base/Resources/assets/js/components/forms/inputs/AppCheckboxInput'
import FormBuilderFieldSet from 'FormBuilder/Resources/assets/js/components/FormBuilderFieldSet'

export default {
  name: 'RequestAccessToUnion',
  components: {
    Multiselect,
    PaymentForm,
    AppCheckboxInput,
    VuePhoneNumberInput,
    FormBuilderFieldSet,
  },
  props: {
    firstName: {
      type: String,
      required: true,
    },
    lastName: {
      type: String,
      required: true,
    },
    email: {
      type: String,
      required: true,
    },
    slug: {
      type: String,
      required: true,
    },
    flatPickerDateFormat: {
      type: String,
      required: true,
    },
    selfAssignableGroupsEnabled: {
      type: Boolean,
      required: true,
    },
    hasMembershipFee: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      errors: null,
      errorMessage: null,
      request: new Form({
        first_name: '',
        last_name: '',
        email: '',
        mobile: '',
        phone_number: '',
        phone_number_country: '',
        opt_in_to_texts: true,
        groups: {
          rank: null,
          nonRank: null,
        },
        membership_fee: {
          setup_intent: {},
        },
      }),
      formBuilderForms: [],
      runPaymentSetup: false,
      processing: false,
      nonRankGroups: [],
      isLoadingNonRankGroups: false,
    }
  },
  async mounted() {
    await this.$nextTick()

    this.request.first_name = this.first_name
    this.request.last_name = this.last_name
    this.request.email = this.email

    this.fetch()
  },
  methods: {
    fetch() {
      this.fetchAccessRequestForms()
      this.handleNonRankSearchChange()
    },
    submit() {
      this.processing = true

      if (this.hasMembershipFee && !this.request.membership_fee.setup_intent.hasOwnProperty('id')) {
        this.runPaymentSetup = true
      } else {
        this.createAccessRequest()
      }
    },
    handleCardSetupConfirmed(data) {
      this.runPaymentSetup = false
      this.request.membership_fee.setup_intent = data.setupIntent

      this.createAccessRequest()
    },
    handlePhoneUpdate(data) {
      this.request.phone_number_country = data.countryCode
    },
    handleCardSetupError() {
      this.runPaymentSetup = false
      this.processing = false
    },
    createAccessRequest() {
      this.errors = null

      this.$api.active().post('membership/access-requests', this.request)
        .then(() => {
          window.location = `${this.$urlGenerator.active()}request-access/success`

          this.runPaymentSetup = false
        }).catch((errors) => {
          this.processing = false
          this.runPaymentSetup = false

          if (errors.response.data.errors) {
            this.errors = errors.response.data.errors

            this.formBuilderForms.forEach((form) => {
              form.submission.setErrors(errors.response.data)
            })
          }

          if (errors.response.data.message) {
            this.errorMessage = errors.response.data.message
          }
        })
    },
    updateSubmission(event) {
      const found = this.formBuilderForms.find(function (element) {
        return element.form.uuid === event.form_id
      })

      if (found) {
        const vm = this

        found.submission = event.submission

        found.form.fields.data.forEach((field) => {
          vm.request[field.attributes.slug] = event.submission[field.attributes.slug]
        })
      }
    },
    fetchAccessRequestForms() {
      const vm = this

      Vue2.prototype.$api.public().get(`unions/${subDomainParser()}/forms/access-request`).then((response) => {
        const accessRequestForms = response.data.data

        vm.formBuilderForms = accessRequestForms.map(function (element) {
          const data = {}

          element.fields.data.forEach((field) => {
            data[field.attributes.slug] = null
          })

          return {
            form: element,
            submission: new Form(data),
          }
        })
      })
    },
    hasErrorFor(key) {
      if (this.errors === null) {
        return false
      }

      return this.errors.hasOwnProperty(key)
    },
    getErrorFor(key) {
      return this.errors[key][0]
    },
    handleNonRankSearchChange: debounce(function (search = '') {
      this.isLoadingNonRankGroups = true

      this.$api.public().get(`unions/${this.slug}/membership-groups?limit=100&only_non_rank=true&query=${search}`).then((response) => {
        this.nonRankGroups = response.data.data.map((group) => {
          return {
            title: group.attributes.title,
            uuid: group.uuid,
          }
        })

        this.isLoadingNonRankGroups = false
      })
    }, 500),
  },
}
</script>

<style>
.input-tel__input,
.country-selector__input {
  border-color: #afb2b3 !important;
}
</style>
